import React, { useContext, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Table, Column, PageSizeType } from 'design-system-web';
import { InspectionInstanceStatus } from 'graphql-query-contracts';
import { useGetInspectionInstances } from 'react-hooks';
import { dateFormatter } from 'async-timing';
import { SIMPLE_DATE } from 'constants-activate';

import { H3 } from 'components/typography';

import ChannelAdminContext from '../../../pages/portal/admin/channel/ChannelAdminContext';

import styles from './styles.scss';

const STATUS_TRANSLATION_KEYS: Record<InspectionInstanceStatus, string> = {
  [InspectionInstanceStatus.Scheduled]:
    'web.admin.inspections.table.statusScheduled',
  [InspectionInstanceStatus.InProgress]:
    'web.admin.inspections.table.statusInProgress',
  [InspectionInstanceStatus.Completed]:
    'web.admin.inspections.table.statusCompleted',
  [InspectionInstanceStatus.Closed]:
    'web.admin.inspections.table.statusClosed',
};

type InspectionInstanceRow = {
  id: string;
  inspectionName: string | null;
  completedByName: string | undefined;
  status: InspectionInstanceStatus;
  startedAt: string | null;
  completedAt: string | null;
};

function mapInstancesToRows(
  instances: ReturnType<typeof useGetInspectionInstances>['instances']
): InspectionInstanceRow[] {
  return instances.map(instance => ({
    id: instance.id,
    inspectionName: instance.templateRef.templateName ?? null,
    completedByName: instance.state.completedByUser?.name,
    status: instance.state.status,
    startedAt: instance.state.startedAt ?? null,
    completedAt: instance.state.completedAt ?? null,
  }));
}

const DEFAULT_PAGE_SIZE: PageSizeType = 50;

export function InspectionsDashboard() {
  const { t } = useTranslation();
  const { channel, timeZone } = useContext(ChannelAdminContext);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(DEFAULT_PAGE_SIZE);

  const { instances, loading, totalCount } = useGetInspectionInstances({
    channelId: channel?._id ?? '',
    start: page * pageSize,
    perPage: pageSize,
  });

  const handlePageSizeChange = (newPageSize: PageSizeType) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const rows = mapInstancesToRows(instances);

  const columns: Column<InspectionInstanceRow>[] = [
    {
      key: 'inspectionName',
      header: t('web.admin.inspections.table.inspectionName'),
    },
    {
      key: 'completedByName',
      header: t('web.admin.inspections.table.completedBy'),
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        row.completedByName ?? t('web.admin.inspections.table.incomplete'),
    },
    {
      key: 'status',
      header: t('web.admin.inspections.table.status'),
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        t(STATUS_TRANSLATION_KEYS[row.status]),
    },
    {
      key: 'startedAt',
      header: t('web.admin.inspections.table.startedOn'),
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        row.startedAt
          ? dateFormatter(row.startedAt, SIMPLE_DATE, timeZone)
          : '',
    },
    {
      key: 'completedAt',
      header: t('web.admin.inspections.table.completedOn'),
      renderCell: (_cell: unknown, row: InspectionInstanceRow) =>
        row.completedAt
          ? dateFormatter(row.completedAt, SIMPLE_DATE, timeZone)
          : '',
    },
  ];

  return (
    <div className={styles.inspectionsDashboard}>
      <div className={styles.titleWrapper}>
        <H3>{t('web.admin.inspections.dashboard.title')}</H3>
      </div>
      <Table
        columns={columns}
        data={rows}
        isLoading={loading}
        disableSorting
        showColumnVisibility
        tableKey="inspections.inspectionsDashboardTable"
        pagination="server"
        totalRows={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
}